/**
 * PlacesLanding — /places
 *
 * World-level directory: continent aggregates + all 249 countries with
 * live discussion counts.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Search, MapPin, ArrowRight, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/geo`;

const CONTINENTS = [
  { slug: "africa",        label: "Africa",         accent: "bg-amber-50 text-amber-700" },
  { slug: "asia",          label: "Asia",           accent: "bg-orange-50 text-orange-700" },
  { slug: "europe",        label: "Europe",         accent: "bg-blue-50 text-blue-700" },
  { slug: "north-america", label: "North America",  accent: "bg-emerald-50 text-emerald-700" },
  { slug: "south-america", label: "South America",  accent: "bg-lime-50 text-lime-700" },
  { slug: "oceania",       label: "Oceania",        accent: "bg-cyan-50 text-cyan-700" },
  { slug: "antarctica",    label: "Antarctica",     accent: "bg-slate-100 text-slate-600" },
];

export default function PlacesLanding() {
  const [continents, setContinents]   = useState([]);
  const [countries, setCountries]     = useState([]);
  const [continent, setContinent]     = useState("");
  const [query, setQuery]             = useState("");
  const [searchResults, setResults]   = useState(null);

  useEffect(() => { (async () => {
    const c = await fetch(`${API}/continents`).then(r => r.json());
    setContinents(c.continents || []);
  })(); }, []);

  useEffect(() => { (async () => {
    const qs = continent ? `?continent=${continent}` : "";
    const c = await fetch(`${API}/countries${qs}`).then(r => r.json());
    setCountries(c.countries || []);
  })(); }, [continent]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      const r = await fetch(`${API}/search?q=${encodeURIComponent(query)}`).then(r => r.json());
      setResults(r.results || []);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const continentsMap = useMemo(() => Object.fromEntries(continents.map(c => [c.slug, c])), [continents]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="places-landing">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
        <Globe className="w-3 h-3" /> World Directory
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Explore every place</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">Drill from continents to countries to states, counties, cities and villages. Every place has a permanent home for its community's conversations.</p>

      <div className="mt-6 relative max-w-xl" data-testid="places-search">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search countries, states, cities…"
          className="w-full pl-11 pr-4 py-3 text-sm border border-slate-200 rounded-full outline-none focus:border-blue-400 bg-white shadow-sm"
          data-testid="places-search-input"
        />
      </div>
      {searchResults && (
        <div className="mt-3 max-w-xl border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden" data-testid="places-search-results">
          {searchResults.length === 0 && (
            <div className="p-3 text-xs text-slate-500">No places found.</div>
          )}
          {searchResults.map((r) => (
            <Link key={r.slug} to={`/places/${r.slug}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50" data-testid={`place-search-${r.slug}`}>
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              <span className="flex-1 text-slate-900">{r.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">{r.kind}</span>
            </Link>
          ))}
        </div>
      )}

      <section className="mt-10">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-bold text-slate-900 text-lg">Continents</h2>
          <span className="text-[11px] text-slate-500" data-testid="heatmap-legend">Tile brightness ∝ discussions</span>
        </div>
        <ContinentHeatmap continents={continents} selected={continent} onSelect={setContinent} />
      </section>

      <section className="mt-10" data-testid="countries-grid">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-bold text-slate-900 text-lg">
            Countries {continent ? <span className="text-xs text-slate-500 font-normal">· {continent.replace("-", " ")}</span> : ""}
          </h2>
          {continent && (
            <button type="button" onClick={() => setContinent("")} className="text-xs font-semibold text-blue-600 hover:underline" data-testid="continent-clear">Clear filter</button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
          {countries.map((c) => (
            <Link
              key={c.slug}
              to={`/places/${c.slug}`}
              data-testid={`country-${c.slug}`}
              className="rounded-lg border border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow-sm transition"
            >
              <div className="text-sm font-semibold text-slate-900 line-clamp-1">{c.name}</div>
              <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                <Users className="w-3 h-3" /> {c.discussions_count || 0} discussions
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function ContinentHeatmap({ continents, selected, onSelect }) {
  const cmap = Object.fromEntries((continents || []).map((c) => [c.slug, c]));
  const maxDisc = Math.max(1, ...(continents || []).map((c) => c.discussions_count || 0));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="continents-heatmap">
      {CONTINENTS.map((c) => {
        const stats  = cmap[c.slug] || { countries: 0, discussions_count: 0 };
        const heat   = maxDisc > 0 ? Math.min(1, (stats.discussions_count || 0) / maxDisc) : 0;
        // Tile blue-ness maps 0..1 → very light → saturated blue.
        const bg     = heat === 0 ? "#f8fafc" : `rgba(37,99,235,${0.10 + heat * 0.80})`;
        const fg     = heat > 0.55 ? "#fff" : "#0f172a";
        const active = selected === c.slug;
        return (
          <button
            key={c.slug}
            type="button"
            onClick={() => onSelect(active ? "" : c.slug)}
            data-testid={`continent-${c.slug}`}
            style={{ backgroundColor: bg, color: fg }}
            className={`rounded-2xl p-4 text-left border transition ${active ? "border-blue-500 shadow-md ring-2 ring-blue-300/40" : "border-slate-200 hover:border-blue-300"}`}
          >
            <div className="font-bold">{c.label}</div>
            <div className="text-[11px] mt-0.5 opacity-90">{stats.countries} countries</div>
            <div className="text-[11px] mt-0.5 opacity-90">{stats.discussions_count} discussions</div>
          </button>
        );
      })}
    </div>
  );
}
